import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ProductReviewsElement, type TProductReviewsElement } from '@shared/dream-components';

import usePaginatedProductReviews from '~/hooks/usePaginatedProductReviews';
import useStaticProductReview from '~/hooks/useStaticProductReview';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { ProductLoaderData } from '~/routes/products/$slug';

interface Props {
  node: TProductReviewsElement;
  children: ReactNode | ReactNode[];
}

const ProductReviews = ({ node, children }: Props) => {
  const { t } = useTranslation();
  const { productReviews, product } = useTypedLoaderData<ProductLoaderData>();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review_id');

  const { reviews, showLoadMore, isLoading, loadMoreReviews, currentPage, totalPages, totalReviews } =
    usePaginatedProductReviews({
      defaultReviews: productReviews?.reviews || [],
      pagination: productReviews?.pagination || null,
      productSlug: product.slug,
      perPage: node.attrs.reviewsPerPage,
      enabled: !reviewId,
    });

  const { data: productReviewData } = useStaticProductReview({
    productSlug: product.slug,
    reviewId,
    enabled: !!reviewId,
  });

  const elementData = useMemo(() => {
    if (!reviewId) {
      return {
        reviews,
        summary: productReviews?.metrics,
        pagination: {
          page: currentPage,
          total: totalReviews,
          total_pages: totalPages,
          per_page: node.attrs.reviewsPerPage || 4,
        },
      };
    }

    return {
      reviews: productReviewData?.review ? [productReviewData.review] : [],
      summary: productReviewData?.metrics,
    };
  }, [
    reviews,
    productReviews,
    currentPage,
    totalReviews,
    totalPages,
    node.attrs.reviewsPerPage,
    productReviewData,
    reviewId,
  ]);

  if (!reviewId && (!productReviews || productReviews?.metrics?.total_reviews_count === 0)) {
    return null;
  }

  return (
    <ProductReviewsElement
      isLive
      showLoadMore={!reviewId && showLoadMore}
      onLoadMoreReviews={loadMoreReviews}
      isLoadingMoreReviews={isLoading}
      element={{
        ...node,
        attrs: {
          ...node.attrs,
          data: elementData,
        },
      }}
      translations={{
        'product_reviews.review': t('product_reviews.review'),
        'product_reviews.reviews': t('product_reviews.reviews'),
        'product_reviews.title': t('product_reviews.title'),
        'product_reviews.load_more': t('product_reviews.load_more'),
        'product_reviews.loading': t('product_reviews.loading'),
      }}
    >
      {children}
    </ProductReviewsElement>
  );
};

export default ProductReviews;
